import {
    EditableEntity,
    ICreatableEntityCollection,
    IEditableEntity,
    IPropEventBroker,
    IReadablePropEventBroker,
} from '@zdrbm/zdr-native-tools';
import { RunStatus } from '../services/AssistantDTOs';
import { IConversationRunContainer } from './containers/ConversationRunContainer';
import { IImage } from '../../../infra';
import { IResource } from 'features/pi/model/resources/Resource';
import { PiError } from 'features/pi/model/pi-errors/PiError';
import type { IFeedback } from './Feedback';

const ClientRunStatus = {
    CLIENT_ERROR: 'CLIENT_ERROR',
};

export const ExtendedRunStatus = { ...RunStatus, ...ClientRunStatus } as const;
export type ExtendedRunStatusType = typeof ExtendedRunStatus[keyof typeof ExtendedRunStatus];
const DONE_STATUSES: ExtendedRunStatusType[] = [
    ExtendedRunStatus.COMPLETED,
    ExtendedRunStatus.REJECTED,
    ExtendedRunStatus.FAILED,
];
export interface IConversationRun extends IEditableEntity {
    updateFrom(otherRun: IConversationRun): void;
    changeStatus(status: ExtendedRunStatusType): boolean;
    input: IReadablePropEventBroker<string>;
    resources: ICreatableEntityCollection<IResource>;
    image: IReadablePropEventBroker<IImage | undefined>;
    conversationId: IReadablePropEventBroker<string>;
    responseContainers: ICreatableEntityCollection<IConversationRunContainer>;
    addContainer(container: IConversationRunContainer): void;
    status: IReadablePropEventBroker<ExtendedRunStatusType>;
    hasReachedTerminalStatus(): boolean;
    feedback: IFeedback;
}

export class ConversationRun
    extends EditableEntity<keyof IConversationRun>
    implements IConversationRun
{
    input: IPropEventBroker<string>;
    image: IPropEventBroker<IImage | undefined>;
    conversationId: IPropEventBroker<string>;
    responseContainers: ICreatableEntityCollection<IConversationRunContainer>;
    resources: ICreatableEntityCollection<IResource>;
    status: IPropEventBroker<ExtendedRunStatusType>;
    feedback: IFeedback;

    getEntityName(): string {
        return 'ConversationRun';
    }
    constructor(
        id: string | undefined,
        conversationId: string,
        input: string,
        responseContainers: IConversationRunContainer[],
        status: ExtendedRunStatusType,
        feedback: IFeedback,
        resources: IResource[] = [],
        image?: IImage,
    ) {
        super(id);
        this.input = this.createPropEventBroker('input', input);
        this.image = this.createPropEventBroker('image', image);
        this.conversationId = this.createPropEventBroker('conversationId', conversationId);
        this.responseContainers = this.createEntityCollection<IConversationRunContainer>(
            'responseContainers',
            responseContainers,
        );
        this.resources = this.createEntityCollection<IResource>('resources', resources);
        this.status = this.createPropEventBroker('status', status);
        this.feedback = feedback;

        this.addSubEntity(this.feedback);

    }

    hasReachedTerminalStatus(): boolean {
        return DONE_STATUSES.includes(this.status.get());
    }

    updateFrom(run: IConversationRun): void {
        if (this.conversationId.get() !== run.conversationId.get()) {
            throw new PiError('ConversationId mismatch');
        }
        this.setId(run.getId());

        run.responseContainers.getOrderedItems().forEach((container, index) => {
            const existingItem = this.responseContainers.getItemAt(index);
            if (existingItem) {
                existingItem.updateFrom(container);
            } else {
                this.responseContainers.addItems(undefined, container);
            }
        });

        run.resources.getOrderedItems().forEach((resource, index) => {
            const existingItem = this.resources.getItemAt(index);
            if (existingItem) {
                existingItem.updateFrom(resource);
            } else {
                this.resources.addItems(undefined, resource);
            }
        });
        this.status.set(run.status.get());
    }

    addContainer(container: IConversationRunContainer): void {
        this.responseContainers.addItems(undefined, container);
    }

    changeStatus(status: ExtendedRunStatusType): boolean {
        return this.status.set(status);
    }
}
